import { toRefs, reactive } from 'vue';
import Api from '../api/Calendar';

const initialState = {
    date: '',
    slotTitle: '',
    isLoadingSlotsForDate: false,
    isRunningBookingRequest: false,
    isRunningCancelRequest: false,
    remainingPackageQuantity: null,
    commentHeader: '',
    scheduledLessonCount: null,
    slotMessage: '',
    responseMessage: '',
    requestSuccess: null,
    shouldScrollToEvents: false,
    showFindNextButton: false,
    slots: [],
    selectedSlot: false,
    isContact: false,
    hasBookingTerms: false,
    teacherId: null,
    productId: null,
    request: null,
    formattedDate: '',
    validRange: {
        start: '',
        end: ''
    },
    booking_terms: '',
    actions: null
};

const state = reactive({ ...initialState });

export default {
    state: toRefs(state),
    getSlots() {
        return state.slots;
    },
    getCurrentDate() {
        return state.date;
    },
    getEventsForDate(date) {
        state.formattedDate = '';
        state.date = date;
        state.isLoadingSlotsForDate = true;
        state.slotMessage = '';
        state.commentHeader = '';
        const currTeacherId = state.teacherId;
        Api.GetSlots(date, currTeacherId, state.productId).then(res => {
            if (state.teacherId !== currTeacherId) {
                // Teacher id was changed, don't display result
                return;
            }
            if (res.success) {
                state.slots = res.data.slots;
            } else {
                state.slotMessage = res.message;
                state.slots = [];
            }
            state.slotTitle = res.title;
            state.showFindNextButton = res.showButton;
            state.booking_terms = res.terms;
            state.formattedDate = res.formattedDate;
            state.commentHeader = res.commentHeader;
        }).catch(err => {
            this.handleError(err)
        }).finally(() => {
            if (state.teacherId !== currTeacherId) {
                // Teacher id was changed, don't display result
                return;
            }
            state.isLoadingSlotsForDate = false;
        })
    },
    setDate(date) {
        state.date = date
    },
    getNextAvailableDay() {
        state.isLoadingSlotsForDate = true;
        state.slotMessage = '';
        const currTeacherId = state.teacherId;

        return state.request = Api.GetNextAvailableDate(state.date, currTeacherId, state.productId)
            .then(res => {
                state.isLoadingSlotsForDate = false;
                if (res.success) {
                    state.date = res.date;
                    this.goToDate(res.date);
                    this.getEventsForDate(res.date);
                } else {
                    state.slotMessage = res.message;
                    state.slots = [];
                }
                state.showFindNextButton = res.showButton;
            }).catch(err => {
                this.handleError(err)
            });
    },
    setValidRange(start, end) {
        state.validRange.start = start;
        state.validRange.end = end;
    },
    setCalendarActions(api) {
        state.actions = api;
    },
    goToDate(date) {
        state.date = date;
        state.actions?.gotoDate(date);
        this.highlightDate(date);
    },
    selectSlot(data) {
        state.selectedSlot = data;
        state.responseMessage = '';
        state.requestSuccess = null;
    },
    handleError(error) {
        console.error(error);
        state.slotMessage = $t('Oops, det skjedde noe galt. Prøv igjen senere.');
        state.slots = [];
        state.showFindNextButton = false;
        state.isLoadingSlotsForDate = false;
    },
    highlightDate(dateStr) {
        const className = 'fc-selected-date';
        const el = document.querySelector('.fc-daygrid-body .' + className);
        if (el !== null) {
            const classNames = el.className;
            const indexOfClass = classNames.indexOf(className);
            if (indexOfClass !== -1) {
                el.className = classNames.substring(0, indexOfClass) + classNames.substring(indexOfClass + className.length);
            }
        }
        const newEl = document.querySelector('.fc-daygrid-body [data-date="' + dateStr + '"]');
        if (newEl !== null) {
            newEl.className += ' ' + className;
        }
    },
    requestBooking(bookingObj) {
        state.isRunningBookingRequest = true;
        Api.Booking(bookingObj).then(res => {
            state.isRunningBookingRequest = false;
            if (res.success) {
                this.getEventsForDate(res.date);
                state.requestSuccess = true;
                state.selectedSlot.comment = bookingObj.comment;
                state.selectedSlot.available = false;
                state.selectedSlot.calendar_id = res.calendar_id;
                state.selectedSlot.product_type = 11;
                if (res.is_email_sent) {
                    state.responseMessage = '<strong>' + $t('Takk!') + '</strong> ' + $t('Forespørselen din har blitt sendt til trafikklærer. Du vil snart motta kvittering via e-post.');
                } else {
                    state.responseMessage = $t('Forespørselen din har blitt sendt til trafikklærer.');
                }
                state.remainingPackageQuantity = res.remainingPackageQuantity;
                state.scheduledLessonCount = res.scheduledLessonCount;
            } else {
                state.responseMessage = res.message;
                state.requestSuccess = false
            }
        }).catch(err => {
            console.error(err);
            state.isRunningBookingRequest = false;
            state.requestSuccess = false;
            state.responseMessage = $t('Her skjedde det noe galt, prøv igjen senere eller kontakt trafikkskolen direkte.')
        })
    },
    cancelBooking(id) {
        state.isRunningCancelRequest = true;

        Api.CancelBooking(id).then(res => {
            if (res.success) {
                this.getEventsForDate(state.date);
                state.selectedSlot.product_type = null;
                state.remainingPackageQuantity = res.remainingPackageQuantity;
                state.scheduledLessonCount = res.scheduledLessonCount;
            }
            state.isRunningCancelRequest = false;
            state.responseMessage = res.message;
            state.requestSuccess = res.success;
        }).catch(err => {
            console.error(err);
            state.isRunningCancelRequest = false;
            state.requestSuccess = false;
            state.responseMessage = $t('Her skjedde det noe galt, prøv igjen senere eller kontakt trafikkskolen direkte.')
        })
    },
    deselectSlot() {
        state.requestSuccess = null;
        this.selectSlot(false);
    },
    scrollToSlots() {
        if (state.shouldScrollToEvents && window.innerWidth <= 720) {
            setTimeout(() => {
                const header = document.getElementById('booking_slots_title');
                header?.scrollIntoView();
            }, 200);
        }
        state.shouldScrollToEvents = true;
    },
    setTeacherId(teacherId) {
        state.teacherId = teacherId;
    },
    getTeacherId() {
        return state.teacherId;
    },
    setProductId(productId) {
        state.productId = productId;
    },
    getProductId() {
        return state.productId;
    },
    getIsLoadingSlotsForDate() {
        return state.isLoadingSlotsForDate;
    },
    getSelectedSlot() {
        return state.selectedSlot;
    },
    getMessage() {
        return state.slotMessage;
    },
    getBookingTerms() {
        return state.booking_terms;
    },
    getRemainingPackageQuantity() {
        return state.remainingPackageQuantity;
    },
    getCommentHeader() {
        return state.commentHeader;
    },
}
